/**
 * Strona Główna – PPHU Imperium
 * Design: Industrial Precision
 * SEO: H1 zawiera usługę + miasto + nazwę firmy
 * Sekcje: Hero, Wyróżniki, Produkty, Opinie, CTA
 */
import { Link } from 'wouter';
import { Phone, ArrowRight, CheckCircle2, Wrench, Shield, Clock } from 'lucide-react';
import type { ReactElement } from 'react';
import { companyData } from '@/theme';
import { useIsMobile } from '@/hooks/useMobile';
import { trpc } from '@/lib/trpc';

const highlights = [
  {
    icon: <Clock size={28} color="#F5C400" />,
    number: '24',
    label: 'lat na rynku',
    desc: 'Działamy od 2002 roku w regionie zachodniopomorskim i pomorskim.',
  },
  {
    icon: <Wrench size={28} color="#F5C400" />,
    number: '9',
    label: 'kategorii produktów',
    desc: 'Elektronarzędzia, pneumatyka, narzędzia warsztatowe, łożyska i więcej.',
  },
  {
    icon: <Shield size={28} color="#F5C400" />,
    number: '100%',
    label: 'oryginalne produkty',
    desc: 'Wyłącznie oryginalne narzędzia Milwaukee i sprawdzonych producentów.',
  },
];


const productCategories = [
  { name: 'Elektronarzędzia Milwaukee', iconId: 'bolt', desc: 'Wiertarki, szlifierki, piły, wkrętarki akumulatorowe' },
  { name: 'Pneumatyka', iconId: 'gear', desc: 'Sprężarki, pistolety pneumatyczne, węże' },
  { name: 'Narzędzia warsztatowe', iconId: 'wrench', desc: 'Klucze, wkrętaki, narzędzia ręczne' },
  { name: 'Materiały ścierne', iconId: 'disc', desc: 'Tarcze, papiery ścierne, ściernice' },
  { name: 'Narzędzia skrawające', iconId: 'drill', desc: 'Wiertła, frezy, płytki skrawające' },
  { name: 'Narzędzia pomiarowe', iconId: 'measure', desc: 'Suwmiarki, mikrometry, poziomice, mierniki' },
  { name: 'Chemia techniczna', iconId: 'flask', desc: 'Smary, oleje, kleje, uszczelniacze' },
  { name: 'Materiały i urządzenia spawalnicze', iconId: 'welding', desc: 'Elektrody, druty, maski i akcesoria' },
  { name: 'Łożyska i elementy złączne', iconId: 'bearing', desc: 'Śruby, nakrętki, łożyska toczne' },
];

const homeGalleryImages = [
  { src: '/manus-storage/milwaukee-panorama_62ab6902.jpg', alt: 'Panorama ekspozycji Milwaukee' },
  { src: '/manus-storage/milwaukee-full-store_bfc06e97.jpg', alt: 'Sklep PPHU Imperium' },
  { src: '/manus-storage/milwaukee-store-wide_d6de3fad.jpg', alt: 'Szerokie ujęcie stoiska' },
  { src: '/manus-storage/milwaukee-store-light_1b9fcea9.jpg', alt: 'Ekspozycja narzędzi Milwaukee' },
  { src: '/manus-storage/milwaukee-store-2_28a22d6b.jpg', alt: 'Stoisko Milwaukee 2' },
  { src: '/manus-storage/milwaukee-store-3_60cac9bf.jpg', alt: 'Stoisko Milwaukee 3' },
  { src: '/manus-storage/milwaukee-store-4_c404afca.jpg', alt: 'Stoisko Milwaukee 4' },
  { src: '/manus-storage/milwaukee-wall-1_a8257b6e.jpg', alt: 'Ściana narzędzi Milwaukee' },
  { src: '/manus-storage/milwaukee-wall-2_cc751dc0.jpg', alt: 'Narzędzia Milwaukee' },
  { src: '/manus-storage/milwaukee-wall-3_169f02d4.jpg', alt: 'Ekspozycja ścienna Milwaukee' },
  { src: '/manus-storage/milwaukee-accessories_361c5b58.jpg', alt: 'Akcesoria Milwaukee' },
  { src: '/manus-storage/milwaukee-sockets_70ee4f7b.jpg', alt: 'Nasadki Milwaukee' },
  { src: '/manus-storage/milwaukee-store-bright_89373426.jpg', alt: 'Sklep Milwaukee' },
];

function TechIcon({ id }: { id: string }) {
  const icons: Record<string, ReactElement> = {
    bolt: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M15 3L5 15h8l-2 8 10-12h-8l2-8z" stroke="#F5C400" strokeWidth="1.5" strokeLinejoin="round"/></svg>
    ),
    gear: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><circle cx="13" cy="13" r="7" stroke="#F5C400" strokeWidth="1.5"/><circle cx="13" cy="13" r="3" stroke="#F5C400" strokeWidth="1.5"/><path d="M13 6V3M13 23v-3M6 13H3M23 13h-3M8.5 8.5L6.5 6.5M19.5 19.5l-2-2M19.5 8.5l-2 2M8.5 19.5l-2-2" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
    wrench: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M8 18L18 8" stroke="#F5C400" strokeWidth="2" strokeLinecap="round"/><path d="M6 8l3 3-2 2-3-3V8h2z" stroke="#F5C400" strokeWidth="1.5" strokeLinejoin="round"/><path d="M18 15l2 2-4 4-2-2 4-4z" stroke="#F5C400" strokeWidth="1.5" strokeLinejoin="round"/></svg>
    ),
    disc: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><circle cx="13" cy="13" r="9" stroke="#F5C400" strokeWidth="1.5"/><circle cx="13" cy="13" r="4" stroke="#F5C400" strokeWidth="1.5"/><circle cx="13" cy="13" r="1.5" fill="#F5C400"/></svg>
    ),
    drill: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M13 4v14M9 8l4-4 4 4" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><rect x="9" y="18" width="8" height="4" rx="1" stroke="#F5C400" strokeWidth="1.5"/></svg>
    ),
    measure: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M5 19L19 5l3 3L8 22H5v-3z" stroke="#F5C400" strokeWidth="1.5" strokeLinejoin="round"/><path d="M14 10l2 2M11 13l2 2M8 16l2 2" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
    flask: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M10 3h6M12 3v7L6.5 19a3 3 0 002.6 4.5h7.8a3 3 0 002.6-4.5L14 10V3" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M8.5 18h9" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
    welding: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M5 20l8-8 4 4-8 8H5v-4zM15 10l3-3 3 3-3 3" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M20 4v-2M23 7h2M20 10v2M17 7h-2" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
    bearing: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><circle cx="13" cy="13" r="8" stroke="#F5C400" strokeWidth="1.5"/><circle cx="13" cy="13" r="3.5" stroke="#F5C400" strokeWidth="1.5"/><path d="M13 5V2M13 24v-3M5 13H2M24 13h-3" stroke="#F5C400" strokeWidth="1.5" strokeLinecap="round"/></svg>
    ),
  };
  return icons[id] || null;
}

export default function Home() {
  const isMobile = useIsMobile();
  const cmsContent = trpc.cms.public.content.useQuery();
  const cmsMedia = trpc.cms.public.media.useQuery();
  const mediaBySlot = new Map((cmsMedia.data ?? []).map(item => [item.slot, item]));
  const contentValue = (key: string, fallback: string) => cmsContent.data?.[key] || fallback;
  const visibleHighlights = highlights.map((highlight, index) => ({
    ...highlight,
    desc: contentValue(`home.highlight.${index}`, highlight.desc),
  }));
  const visibleProductCategories = productCategories.map((category, index) => ({
    ...category,
    desc: contentValue(`home.category.${index}`, category.desc),
  }));
  const homeHeroImage = mediaBySlot.get('home-hero')?.url || '/manus-storage/milwaukee-panorama_62ab6902.jpg';
  const galleryImages = homeGalleryImages.map((image, index) => {
    const replacement = mediaBySlot.get(`gallery-${index + 1}`);
    return replacement ? { src: replacement.url, alt: replacement.altText } : image;
  });
  return (
    <div style={{ minHeight: '100vh' }}>

      {/* ============================================================
         HERO SECTION
         ============================================================ */}
     <section
       style={{
         position: 'relative',
         backgroundColor: '#111B30',
         overflow: 'hidden',
         minHeight: isMobile ? 'auto' : '88vh',
         display: 'flex',
         flexDirection: isMobile ? 'column' : 'row',
         alignItems: isMobile ? 'stretch' : 'center',
       }}
     >
        {/* LEFT PANEL – text */}
        <div style={{
          flex: isMobile ? 'none' : '0 0 58%',
          width: isMobile ? '100%' : undefined,
          backgroundColor: '#111B30',
          minHeight: isMobile ? 'auto' : '88vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isMobile ? '1.25rem' : 'max(2rem, calc((100vw - 1280px) / 2 + 2rem))',
          paddingRight: isMobile ? '1.25rem' : '4rem',
          paddingTop: isMobile ? '5.5rem' : '5rem',
          paddingBottom: isMobile ? '2rem' : '5rem',
        }}>
          {/* Yellow accent bar */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: '#F5C400',
          }} aria-hidden="true" />

          {/* Grid overlay on left panel */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(245,196,0,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(245,196,0,0.025) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            pointerEvents: 'none',
          }} aria-hidden="true" />

          <div style={{ maxWidth: '600px', position: 'relative' }}>
            {/* Label */}
            <div style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
             <span style={{
               fontFamily: "'Manrope', sans-serif",
               fontWeight: 800,
                fontSize: 'clamp(1.92rem, 4vw, 3.36rem)',
               letterSpacing: '0.08em',
               textTransform: 'uppercase',
               color: '#E31837',
               display: 'block',
             }}>Milwaukee</span>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#F5C400',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                Autoryzowany punkt sprzedaży Sławno
              </span>
            </div>

            {/* H1 */}
            <h1 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(1.92rem, 4vw, 3.36rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              color: '#F8F8F6',
              marginBottom: '1.5rem',
            }}>
              {/* Elektronarzędzia – długie słowo, -10% */}
              <span style={{ display: 'block', fontSize: '0.9em' }}>Elektronarzędzia</span>
              {/* Pneumatyka */}
              <span style={{ display: 'block' }}>Pneumatyka</span>
              {/* Narzędzia warsztatowe – długie, -10% */}
              <span style={{ display: 'block', fontSize: '0.9em' }}>Narzędzia warsztatowe</span>
              {/* PPHU Imperium · Sławno – mniejsze, przygaszone */}
            </h1>

            {/* Lead */}
            <p style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(248,248,246,0.45)',
              marginBottom: '0.5rem',
            }}>
              Szeroki asortyment
            </p>
            <p style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
              lineHeight: 1.8,
              color: 'rgba(248,248,246,0.7)',
              marginBottom: '0.25rem',
              maxWidth: '520px',
            }}>
              {contentValue('home.hero.primary', 'Profesjonalne wyposażenie techniczne.')}
            </p>
            <p style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
              lineHeight: 1.8,
              color: 'rgba(248,248,246,0.7)',
              marginBottom: '2rem',
              maxWidth: '520px',
            }}>
              {contentValue('home.hero.secondary', 'Obsługujemy firmy i klientów indywidualnych z całego Pomorza.')}
            </p>

            {/* Trust bullets */}
            <div style={{ marginBottom: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {[
                '24 lata na rynku w regionie zachodniopomorskim',
                'Autoryzowany punkt sprzedaży Milwaukee',
                'Obsługa firm produkcyjnych i klientów prywatnych',
              ].map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <CheckCircle2 size={16} color="#F5C400" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.875rem', color: 'rgba(248,248,246,0.65)', fontFamily: "'Manrope', sans-serif" }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
              <Link href="/kontakt" className="btn-primary" style={{ fontSize: '1rem' }}>
                Zamów wycenę
                <ArrowRight size={18} />
              </Link>
              <a href={`tel:${companyData.phoneRaw}`} className="btn-secondary" style={{ fontSize: '1rem' }}>
                <Phone size={16} />
                {companyData.phone}
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL – photo */}
        <div style={{
          flex: isMobile ? 'none' : '0 0 42%',
          width: isMobile ? '100%' : undefined,
          height: isMobile ? '240px' : undefined,
          minHeight: isMobile ? 'auto' : '88vh',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Diagonal cut on left edge */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${homeHeroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            clipPath: isMobile ? 'none' : 'polygon(8% 0, 100% 0, 100% 100%, 0% 100%)',
          }} aria-hidden="true" />
          {/* Dark overlay on photo */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: isMobile
              ? 'linear-gradient(to bottom, rgba(17,27,48,0.5) 0%, rgba(17,27,48,0.1) 100%)'
              : 'linear-gradient(to right, rgba(17,27,48,0.35) 0%, rgba(17,27,48,0.05) 60%)',
            clipPath: isMobile ? 'none' : 'polygon(8% 0, 100% 0, 100% 100%, 0% 100%)',
          }} aria-hidden="true" />
        </div>
      </section>

      {/* ============================================================
          STATS BAR – białe tło, 4 liczby
          ============================================================ */}
      <section style={{ backgroundColor: '#fff', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: isMobile ? '0 0.5rem' : '0 2rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        }}>
          {[
            { number: '24', suffix: '', label: 'Lat na rynku', sub: 'w regionie zachodniopomorskim' },
            { number: '9',   suffix: '', label: 'Kategorii produktów', sub: 'od elektronarzędzi po łożyska' },
            { number: '5,0', suffix: '★', label: 'Ocena Google', sub: '' },
            { number: '1 - 3', suffix: 'dni', label: 'Czas odpowiedzi', sub: '' },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                padding: isMobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem',
                borderRight: isMobile
                  ? (i % 2 === 0 ? '1px solid #E5E7EB' : 'none')
                  : (i < 3 ? '1px solid #E5E7EB' : 'none'),
                borderBottom: isMobile && i < 2 ? '1px solid #E5E7EB' : 'none',
                textAlign: 'center',
              }}
            >
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                lineHeight: 1,
                color: '#0D0D0D',
                letterSpacing: '-0.02em',
                marginBottom: '0.5rem',
              }}>
                {stat.number}
                {/* Darker gold than the brand yellow specifically here - #F5C400
                    on this section's white background fails WCAG contrast
                    (~1.6:1); this variant clears the 3:1 large-text minimum.
                    Every other use of #F5C400 elsewhere sits on the dark navy
                    background and already passes, so left untouched. */}
                <span style={{ color: '#B8860B', fontSize: '0.65em' }}>{stat.suffix}</span>
              </div>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#1A2744',
                marginBottom: '0.25rem',
              }}>
                {stat.label}
              </div>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '0.78rem',
                color: '#6B7280',
                lineHeight: 1.4,
              }}>
                {stat.sub}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================
          WYRÓŻNIKI – "Dlaczego my"
          ============================================================ */}
      <section style={{ backgroundColor: '#1A2744', padding: '2.5rem 0' }}>
        <div className="container">
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="section-label">Dlaczego my</div>
            <h2 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
              textTransform: 'uppercase',
              color: '#F8F8F6',
              letterSpacing: '-0.01em',
              maxWidth: '500px',
            }}>
              Fakty, nie obietnice
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.5px',
            backgroundColor: 'rgba(245,196,0,0.15)',
          }}>
            {visibleHighlights.map((h, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: '#1A2744',
                  padding: '1.25rem 1.25rem',
                  position: 'relative',
                }}
              >
                <div style={{ marginBottom: '0.5rem' }}>{h.icon}</div>
                <div style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 800,
                  fontSize: '2.25rem',
                  lineHeight: 1,
                  color: '#F5C400',
                  marginBottom: '0.15rem',
                }}>
                  {h.number}
                </div>
                <div style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: '#F8F8F6',
                  marginBottom: '0.4rem',
                }}>
                  {h.label}
                </div>
                <p style={{
                  fontSize: '0.78rem',
                  color: 'rgba(248,248,246,0.55)',
                  lineHeight: 1.6,
                  margin: 0,
                }}>
                  {h.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUKTY – Milwaukee showcase
          ============================================================ */}
      <section style={{ backgroundColor: '#F8F8F6', padding: '5rem 0' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '3rem',
            alignItems: 'center',
          }}>
            {/* Text */}
            <div>
              <div className="section-label" style={{ color: '#1A2744' }}>
                <span style={{ backgroundColor: '#1A2744', width: '24px', height: '3px', display: 'block' }} />
                Nasz asortyment
              </div>
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                textTransform: 'uppercase',
                color: '#0D0D0D',
                letterSpacing: '-0.01em',
                marginBottom: '1.25rem',
              }}>
                {contentValue('home.assortment.title', 'Pełne wyposażenie warsztatu w jednym miejscu')}
              </h2>
              <p style={{
                fontSize: '1rem',
                color: '#4B5563',
                lineHeight: 1.75,
                marginBottom: '2rem',
              }}>
                {contentValue('home.assortment.description', 'Od elektronarzędzi Milwaukee przez pneumatykę, po łożyska i elementy złączne. Obsługujemy zakłady produkcyjne, firmy budowlane i klientów indywidualnych z województwa zachodniopomorskiego i pomorskiego.')}
              </p>
         <Link href="/oferta#asortyment" className="btn-primary" style={{ backgroundColor: '#1A2744', color: '#F5C400' }}>
              Zobacz pełną ofertę
              <ArrowRight size={16} />
            </Link>
            </div>

            {/* Product grid */}
            <div className="home-product-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '1px',
              backgroundColor: '#E5E7EB',
            }}>
              {visibleProductCategories.map((cat, i) => (
                <a
                  key={i}
                  href={`/oferta#kat-${i}`}
                  className={i === productCategories.length - 1 ? 'home-product-card--last' : undefined}
                  style={{
                    backgroundColor: '#fff',
                    padding: '1.25rem',
                    transition: 'background-color 200ms ease',
                    display: 'block',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#FFF9E0'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; }}
                >
                  <div style={{ marginBottom: '0.625rem' }}><TechIcon id={cat.iconId} /></div>
                  <div style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#0D0D0D',
                    marginBottom: '0.25rem',
                  }}>
                    {cat.name}
                  </div>
                 <div style={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.5 }}>
                   {cat.desc}
                 </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          GALERIA ZDJĘĆ SKLEPU
          ============================================================ */}
      <section style={{ backgroundColor: '#F8F8F6', padding: '4rem 0', overflow: 'hidden' }}>
        <div className="container" style={{ marginBottom: '2rem' }}>
          <div style={{ maxWidth: '520px' }}>
            <div style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
              textTransform: 'uppercase',
              color: '#0D0D0D',
              lineHeight: 1.1,
              marginBottom: '1rem',
            }}>
              Autoryzowany punkt sprzedaży{' '}
              <span style={{ color: '#C8102E' }}>Milwaukee</span>
            </div>
            <p style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: '0.95rem',
              color: 'rgba(13,13,13,0.65)',
              lineHeight: 1.7,
            }}>
              Największy wybór elektronarzędzi Milwaukee w regionie Sławna.
              Wiertarki, szlifierki, piły, wkrętarki i akcesoria w stałej sprzedaży.
            </p>
          </div>
        </div>
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            animation: 'galleryScroll 32s linear infinite',
            width: 'max-content',
          }}>
            {[...galleryImages, ...galleryImages].map((img, i) => (
              <div key={i} style={{
                flexShrink: 0,
                width: '320px',
                height: '220px',
                borderRadius: '3px',
                overflow: 'hidden',
                border: '2px solid rgba(245,196,0,0.2)',
              }}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  width={320}
                  height={220}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ============================================================
          CTA SECTION
          ============================================================ */}
      <section style={{
        backgroundColor: '#F5C400',
        padding: '4rem 0',
      }}>
        <div className="container">
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '2rem',
          }}>
            <div>
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                textTransform: 'uppercase',
                color: '#0D0D0D',
                letterSpacing: '-0.01em',
                marginBottom: '0.5rem',
              }}>
                Potrzebujesz narzędzi?
              </h2>
              <p style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '1rem',
                color: 'rgba(13,13,13,0.7)',
                margin: 0,
              }}>
                Zadzwoń lub napisz – odpowiemy tak szybko jak to możliwe.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <a
                href={`tel:${companyData.phoneRaw}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#0D0D0D',
                  color: '#F5C400',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '0.875rem 2rem',
                  borderRadius: '2px',
                  textDecoration: 'none',
                  transition: 'transform 150ms ease',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'none'; }}
              >
                <Phone size={18} />
                {companyData.phone}
              </a>
              <Link
                href="/kontakt"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: 'transparent',
                  color: '#0D0D0D',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '0.875rem 2rem',
                  borderRadius: '2px',
                  border: '2px solid #0D0D0D',
                  textDecoration: 'none',
                  transition: 'all 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#0D0D0D';
                  (e.currentTarget as HTMLElement).style.color = '#F5C400';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#0D0D0D';
                }}
              >
                Zamów wycenę
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
